import type { TmsFactoringBatchable, TmsInvoiceRow } from '@numeo/types-main-web-app'
import { create } from 'zustand'
import { readCard } from '../api/writeCardApi'

interface FactoringBatchableState {
    /** The company's invoices a new factoring batch takes now (the oldest 50), as the server read them; empty before the first read. */
    rows: TmsInvoiceRow[]
    /** Their whole count, or null before a read succeeded (the offer then counts only the rows on screen). */
    total: number | null
    /** Where the read stands: the offer says it is checking, or that it could not check, rather than "nothing is eligible". */
    status: 'idle' | 'loading' | 'ready' | 'failed'
    /**
     * Reads them again. Calls made while a read runs share it, so the tables drawn at once cost one read; `afterWrite` (a write just
     * changed them) reads once more after a running read, which may have started before the write.
     */
    refresh: (afterWrite?: boolean) => Promise<void>
    /** Forgets the answer, for another user or company in the same tab (a logout does not reload the page); a read still running is dropped. */
    reset: () => void
}

let inflight: Promise<void> | null = null
/** Bumped by `reset`: a read started before it answers for the previous owner and is dropped. */
let generation = 0

/**
 * The company-wide factoring eligibility the chat's factoring offer counts (S17): an invoice the answer's table does not list is still an
 * invoice a batch takes. Read with `tms_invoices.worklist` `factoring_batchable`, the filter `prepare_batch` groups by. A failed read keeps
 * the last answer (or none): the offer then falls back to the rows on screen, never to a guess.
 */
export const useFactoringBatchableStore = create<FactoringBatchableState>()((set, get) => ({
    rows: [],
    total: null,
    status: 'idle',
    refresh: (afterWrite = false) => {
        if (inflight) return afterWrite ? inflight.then(() => get().refresh()) : inflight
        // Started inside the promise, so a read that throws at once is a failed read like any other, never the caller's error.
        // A read after a good one keeps its answer showing while it runs; only the first read says it is checking.
        if (get().status !== 'ready') set({ status: 'loading' })
        const started = generation
        const read: Promise<void> = Promise.resolve()
            .then(() => readCard<TmsFactoringBatchable>('tms_invoices.worklist', { factoring_batchable: true }))
            .then(({ factoring_batchable }) => {
                if (started === generation) set({ rows: factoring_batchable.rows, total: factoring_batchable.total, status: 'ready' })
            })
            .catch(() => {
                if (started === generation && get().status !== 'ready') set({ status: 'failed' })
            })
            .finally(() => {
                if (inflight === read) inflight = null
            })
        inflight = read
        return read
    },
    reset: () => {
        generation++
        inflight = null
        set({ rows: [], total: null, status: 'idle' })
    },
}))
